import { Injectable, signal, computed } from '@angular/core';
import { FlatTranslation } from '../types/language';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  currentLanguage = signal<'en' | 'ko' | 'vi'>('en');

  private translations = signal<any>({
    en: {
      sidebar: {
        dashboard: 'Dashboard',
        pos: 'POS (Sales)',
        reservation: 'Reservation Mgmt',
        salesInquiry: 'Sales Inquiry',
        membership: 'Membership Mgmt',
        cancellation: 'Cancellation Mgmt',
        attendance: 'Attendance Mgmt',
        serviceEnv: 'Service Env',
        settings: 'Settings',
        storeSettings: 'Store Settings',
        serviceSettings: 'Service Settings',
        languageSettings: 'Language Settings',
        logout: 'LOGOUT',
        vanConnection: 'VAN Connection',
        online: 'Online: TERM-01',
        adminUser: 'Admin User',
      },
      header: {
        systemOnline: 'SYSTEM ONLINE',
      },
      dashboard: {
        weeklySalesStats: 'Weekly Sales Statistics',
        card: 'Card',
        cash: 'Cash',
        other: 'Other',
        storeSummary: 'Store Summary',
        storeName: 'Store Name',
        weeklyAvgSales: 'Weekly Avg Sales',
        currentSales: 'Current Sales',
        popularServices: 'Popular Services',
        popularOptions: 'Popular Options',
        reservationStats: 'Reservation Statistics',
        weeklyReservations: 'Weekly Reservations',
        prevWeek: 'Prev Week',
        weeklyCancellations: 'Weekly Cancellations',
        notices: 'Notices',
        no: 'No.',
        title: 'Title',
        date: 'Date',
      },
      pos: {
        dog: 'Dog',
        cat: 'Cat',
        option: 'Option',
        search: 'Search...',
        goBack: 'Go Back',
        receiveAmount: 'Receive Amount',
        receivedAmount: 'Received Amount',
        paymentAmount: 'Payment Amount',
        cardPaymentAmount: 'Card Payment Amount',
        cashPaymentAmount: 'Cash Payment Amount',
        easyPayAmount: 'EasyPay Amount',
        prepaymentAmount: 'Prepayment Amount',
        processCardPayment: 'Process Card Payment',
        cash: 'Cash',
        cashReceipt: 'Cash Receipt',
        processEasyPay: 'Process EasyPay',
        usePrepayment: 'Use Prepayment',
        checkBalance: 'Check Balance',
        paymentCompleted: 'Payment Completed',
        countdown: 'Screen will close in {{countdown}} seconds...',
        closeNow: 'Close Now',
        paymentMethods: {
          card: 'Card',
          cash: 'Cash',
          easyPay: 'EasyPay',
          points: 'Points',
          cardDesc: 'Credit/Debit Card',
          cashDesc: 'Pay with banknotes',
          easyPayDesc: 'Mobile Payment',
          pointsDesc: 'Prepayment / Points',
          proceed: 'Proceed',
        }
      },
      cart: {
        currentOrder: 'Current Order',
        staff: 'Staff',
        change: 'Change',
        empty: 'Your cart is empty',
        emptySub: 'Select products to get started',
        totalItems: 'Total Items',
        discount: 'Discount',
        totalSales: 'Total Sales',
        totalDiscount: 'Total Discount',
        receiveAmount: 'Receive Amount',
        charge: 'Charge',
      },
      product: {
        add: 'Add',
        description: 'Description',
        close: 'Close',
        addToCart: 'Add to Cart',
      },
      reservation: {
        title: 'Reservation Management',
        tabMgmt: 'Reservation',
        tabHistory: 'Reservation History',
        register: 'Register Reservation',
        date: 'Date',
        today: 'Today',
        oneWeek: '1 Week',
        initialize: 'Initialize',
        inquiry: 'Inquiry',
        delete: 'Delete',
        save: 'Save',
        availableTime: 'Available Time',
        reservedService: 'Reserved Service',
        totalWorkTime: 'Total Work Time',
        noServiceSelected: 'No services selected',
        history: {
          title: 'Reservation History',
          oneMonth: '1 Month',
          cancel: 'Cancel Reservation',
          no: 'NO',
          time: 'Time',
          customer: 'Customer',
          phone: 'Phone',
          service: 'Service',
          staff: 'Staff',
          status: 'Status',
          amount: 'Amount',
          statuses: {
            reserved: 'Reserved',
            completed: 'Completed',
            cancelled: 'Cancelled',
            noShow: 'NoShow',
          },
        },
      },
      sales: {
        title: 'Sales Management',
        tabs: {
          sales: 'Sales Inquiry',
          receipt: 'Receipt Inquiry',
          staff: 'Staff Sales Inquiry',
        },
        date: 'Business Date',
        today: 'Today',
        oneWeek: '1 Week',
        oneMonth: '1 Month',
        threeMonths: '3 Months',
        initialize: 'Reset',
        inquiry: 'Inquiry',
        salesTable: {
          no: 'NO',
          businessDate: 'Business Date',
          salesStatus: 'Sales Status',
          paymentMethods: 'Payment Methods',
          totalSales: 'Total Sales',
          totalDiscount: 'Total Disc',
          netSales: 'Net Sales',
          receiptCount: 'Receipts',
          avgValue: 'Avg Value',
          cancelCount: 'Cancel Count',
          cancelAmount: 'Cancel Amt',
          cash: 'Cash',
          card: 'Card',
          easyPay: 'Easy Pay',
          prepay: 'Prepay',
          other: 'Other',
        },
        receipt: {
          list: 'Receipt List',
          print: 'Print Receipt',
          details: 'Receipt Details',
          no: 'NO',
          businessDate: 'Business Date',
          receiptNo: 'Receipt No',
          amount: 'Amount',
          phone: 'Phone Number',
          type: 'Type',
          normal: 'Normal',
          return: 'Return',
          preview: {
            title: '[ RECEIPT ]',
            store: '[Store]',
            bizNo: '[Biz No]',
            addr: '[Addr]',
            ceo: '[CEO]',
            tel: '[TEL]',
            date: '[Date]',
            posNo: '[POS No]',
            item: 'Item',
            price: 'Price',
            qty: 'Qty',
            amt: 'Amt',
            totalSales: 'Total Sales:',
            vat: 'VAT:',
            service: 'Service:',
            total: 'Total:',
            card: 'Card:',
            cardNo: 'No:',
            apprNo: 'Appr No:',
            merchantNo: 'sales.receipt.preview.merchantNo',
            apprDate: 'Appr Date:',
          }
        },
        staff: {
          title: 'Staff Sales',
          staff: 'Staff',
          all: 'All',
          salesByStaff: 'Sales by Staff',
          salesDetails: 'Sales Details',
          no: 'NO',
          name: 'Name',
          receipts: 'Receipts',
          totalSales: 'Total Sales',
          netSales: 'Net Sales',
          date: 'Date',
          receiptNo: 'Receipt No.',
          type: 'Type',
          total: 'Total',
          discount: 'Discount',
          net: 'Net',
          normal: 'Normal',
          return: 'Return',
        },
      },
      membership: {
        title: 'Membership Management',
        tabs: {
          customer: 'Customer Management',
          prepayment: 'Prepayment Management',
        },
        customer: {
          phone: 'Phone Number',
          name: 'Name',
          reset: 'Reset',
          inquiry: 'Inquiry',
          delete: 'Delete',
          newMember: 'New Member',
          no: 'NO',
          customerNo: 'Customer No.',
          grade: 'Grade',
          pointDetails: 'Point Details',
          accumulated: 'Accumulated',
          used: 'Used',
          available: 'Available',
          visitCount: 'Visit Count',
          prepaidBalance: 'Prepaid Balance',
          joinDate: 'First Join Date',
          showing: 'Showing',
          to: 'to',
          of: 'of',
          entries: 'entries',
        },
        modal: {
          title: 'Customer Registration',
          customerNo: 'Customer No.',
          name: 'Name',
          phone: 'Phone',
          sms: 'SMS Reception',
          receive: 'Receive',
          reject: 'Reject',
          cancel: 'Cancel',
          save: 'Save',
        },
        prepayment: {
          title: 'Prepayment Management',
          initialize: 'Initialize',
          inquiry: 'Inquiry',
          phone: 'Phone',
          name: 'Name',
          customerInfo: 'Customer Info',
          grade: 'Grade',
          balance: 'Prepaid Balance',
          charge: 'Charge',
          cancelCharge: 'Cancel Charge',
          chargeAmount: 'Charge Amount',
          balanceAfter: 'Balance After',
          prepaymentHistory: 'Prepayment History',
          usageHistory: 'Usage History',
          date: 'Date',
          type: 'Type',
          total: 'Total',
          cancelPossible: 'Cancel Possible',
          possible: 'Possible',
          impossible: 'Impossible',
          receiptNo: 'Receipt No.',
          usageAmount: 'Usage Amount',
        }
      },
      settings: {
        title: 'Environment Settings - Store Management',
        tabs: {
          store: 'Store Env Settings',
          staff: 'Staff Management',
          members: 'Member Management',
          hours: 'Business Hours',
        },
        store: {
          repImage: 'Representative Image',
          coverImages: 'Cover Images',
          add: 'Add',
          basicInfo: 'Basic Info',
          edit: 'Edit',
          save: 'Save',
          storeCodeName: 'Store Code/Name',
          repName: 'Rep Name',
          bizLicense: 'Biz License No.',
          businessName: 'Business Name',
          bizType: 'Biz Type',
          phone: 'Phone',
          mobile: 'Mobile',
          address: 'Address',
          search: 'Search',
          deviceConfig: 'Device Configuration',
          vanCode: 'VAN Code',
          tid: 'TID',
          comport: 'COM Port',
          baudRate: 'Baud Rate',
          depositUsage: 'Deposit Usage',
          depositHelp: 'Enable deposit requirement for reservations',
        },
        staff: {
          title: 'Staff Management',
          newReset: 'New/Reset',
          delete: 'Delete',
          save: 'Save',
          searchPlaceholder: 'Search by name, ID, phone...',
          nameId: 'Name / ID',
          status: 'Status',
          userInfo: 'User Info',
          workHours: 'Work Hours',
          userInfoHelp: 'Manage personal and login details',
          workHoursHelp: 'Weekly schedule configuration',
          accountDetails: 'Account Details',
          userId: 'User ID',
          check: 'Check',
          password: 'Password',
          authority: 'Authority',
          admin: 'Administrator',
          user: 'Standard User',
          workStatus: 'Work Status',
          active: 'Active (Employed)',
          inactive: 'Inactive (Retired)',
          personalInfo: 'Personal Information',
          staffName: 'Staff Name',
          staffNo: 'Staff No.',
          phone: 'Phone',
          email: 'Email',
          address: 'Address',
          days: {
            mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday'
          }
        },
        members: {
          accDiscType: 'Accumulation / Discount Class',
          accumulation: 'Accumulation',
          discount: 'Discount',
          view: 'View',
          save: 'Save',
          membershipLevels: 'Membership Levels',
          delete: 'Delete',
          add: 'Add',
          levelName: 'Level Name',
          criteriaAmount: 'Criteria Amount',
          criteriaCount: 'Count Criteria',
          accRate: 'Acc Rate',
          discRate: 'Disc Rate',
          flatRateMgmt: 'Flat Rate Management',
          policyName: 'Policy Name',
          paymentAmount: 'Payment Amount',
          accPoints: 'Acc Points',
          validityPeriod: 'Validity Period',
          won: 'won'
        },
        hours: {
          title: 'Environment Settings - Business Hours',
          settings: 'Business Hours Settings',
          schedule: 'Monthly Schedule',
          save: 'Save',
          operatingHours: 'OPERATING HOURS',
          breakTime: 'BREAK TIME',
          annualTitle: 'Annual Holiday Settings',
          annualHelp: 'Configure recurring weekly holidays',
          every: 'Every',
          closed: 'Closed'
        }
      },
      serviceSettings: {
        title: 'Environment Settings - Service Management',
        tabs: {
          service: 'Service Settings',
          option: 'Option Settings'
        },
        mainCategory: 'Main Category',
        subCategory: 'Sub Category',
        optionSettings: 'Option Settings',
        categoryName: 'CATEGORY NAME',
        usage: 'USAGE',
        sameAsMain: 'Same as Main Category',
        serviceName: 'SERVICE NAME',
        time: 'TIME',
        amount: 'AMOUNT',
        use: 'Use',
        unused: 'Unused',
        noGroups: 'No groups',
        noCategories: 'No categories',
        noOptions: 'No options available',
      },
      cancellation: {
        title: 'Cancellation Management',
        search: {
          placeholder: 'Search by name, phone, receipt no...',
        },
        actions: {
          reprint: 'Reprint Receipt',
          cancel: 'Process Cancellation',
        },
        table: {
          no: 'No.',
          date: 'Date/Time',
          receiptNo: 'Receipt No.',
          customer: 'Customer',
          phone: 'Phone',
          staff: 'Staff',
          status: 'Status',
          amount: 'Amount',
        },
        status: {
          completed: 'Completed',
          pending: 'Pending',
        }
      },
      profile: {
        title: 'My Profile',
        userInfo: 'User Information',
        security: 'Security',
        fullName: 'Full Name',
        email: 'Email',
        storeId: 'Store ID',
        updateProfile: 'Update Profile',
        changePassword: 'Change Password',
        currentPassword: 'Current Password',
        newPassword: 'New Password',
        confirmPassword: 'Confirm Password',
        updatePassword: 'Update Password',
        edit: 'Edit',
        save: 'Save',
        cancel: 'Cancel',
      },
      common: {
        save: 'Save',
        cancel: 'Cancel',
        edit: 'Edit',
        delete: 'Delete',
        search: 'Search...',
        actions: 'Actions',
        reset: 'Reset',
        close: 'Close',
        view: 'View',
        printReceipt: 'Print Receipt',
      },
      language: {
        title: 'Language Management',
        addKey: 'Add New Key',
        editKey: 'Edit Translation',
        key: 'Translation Key',
        keyHelp: 'Use dot notation for nesting (e.g., common.save). Key cannot be changed after creation.',
        enValue: 'English Value',
        krValue: 'Korean Value',
        searchPlaceholder: 'Search records...',
      },
    },
    ko: {
      sidebar: {
        dashboard: '대시보드',
        pos: 'POS (매출)',
        reservation: '예약 관리',
        salesInquiry: '매출 조회',
        membership: '회원 관리',
        cancellation: '취소 관리',
        attendance: '출근 관리',
        serviceEnv: '서비스 환경',
        settings: '환경 설정',
        storeSettings: '매장 설정',
        serviceSettings: '서비스 설정',
        languageSettings: '언어 설정',
        logout: '로그아웃',
        vanConnection: 'VAN 연결',
        online: '온라인: TERM-01',
        adminUser: '관리자',
      },
      header: {
        systemOnline: '시스템 온라인',
      },
      dashboard: {
        weeklySalesStats: '주간 매출 통계',
        card: '카드',
        cash: '현금',
        other: '기타',
        storeSummary: '매장 요약',
        storeName: '매장 이름',
        weeklyAvgSales: '주간 평균 매출',
        currentSales: '현재 매출',
        popularServices: '인기 서비스',
        popularOptions: '인기 옵션',
        reservationStats: '예약 통계',
        weeklyReservations: '주간 예약',
        prevWeek: '지난 주',
        weeklyCancellations: '주간 취소',
        notices: '공지사항',
        no: '번호',
        title: '제목',
        date: '날짜',
      },
      pos: {
        dog: '강아지',
        cat: '고양이',
        option: '옵션',
        search: '검색...',
        goBack: '뒤로 가기',
        receiveAmount: '받을 금액',
        receivedAmount: '받은 금액',
        paymentAmount: '결제할 금액',
        cardPaymentAmount: '카드 결제 금액',
        cashPaymentAmount: '현금 결제 금액',
        easyPayAmount: '간편결제 금액',
        prepaymentAmount: '선결제 금액',
        processCardPayment: '카드 결제 진행',
        cash: '현금',
        cashReceipt: '현금영수증',
        processEasyPay: '간편결제 진행',
        usePrepayment: '선결제 사용',
        checkBalance: '잔액 확인',
        paymentCompleted: '결제 완료',
        countdown: '{{countdown}}초 후에 화면이 닫힙니다...',
        closeNow: '지금 닫기',
        paymentMethods: {
          card: '카드',
          cash: '현금',
          easyPay: '간편결제',
          points: '포인트',
          cardDesc: '신용/체크카드',
          cashDesc: '지폐로 결제',
          easyPayDesc: '모바일 결제',
          pointsDesc: '선불금/포인트',
          proceed: '진행',
        }
      },
      cart: {
        currentOrder: '현재 주문',
        staff: '담당자',
        change: '변경',
        empty: '장바구니가 비어있습니다',
        emptySub: '시작하려면 상품을 선택하세요',
        totalItems: '총 수량',
        discount: '할인',
        totalSales: '총 매출',
        totalDiscount: '총 할인',
        receiveAmount: '받을 금액',
        charge: '결제',
      },
      product: {
        add: '추가',
        description: '설명',
        close: '닫기',
        addToCart: '카트에 추가',
      },
      reservation: {
        title: '예약 관리',
        tabMgmt: '예약',
        tabHistory: '예약 내역',
        register: '예약 등록',
        date: '날짜',
        today: '오늘',
        oneWeek: '1주',
        initialize: '초기화',
        inquiry: '조회',
        delete: '삭제',
        save: '저장',
        availableTime: '가능 시간',
        reservedService: '예약된 서비스',
        totalWorkTime: '총 작업 시간',
        noServiceSelected: '선택된 서비스 없음',
        history: {
          title: '예약 내역',
          oneMonth: '1개월',
          cancel: '예약 취소',
          no: '번호',
          time: '시간',
          customer: '고객',
          phone: '전화번호',
          service: '서비스',
          staff: '담당자',
          status: '상태',
          amount: '금액',
          statuses: {
            reserved: '예약',
            completed: '완료',
            cancelled: '취소',
            noShow: '노쇼',
          },
        },
      },
      sales: {
        title: '매출 관리',
        tabs: {
          sales: '매출조회',
          receipt: '영수증별조회',
          staff: '직원별매출조회',
        },
        date: '영업일자',
        today: '오늘',
        oneWeek: '일주일',
        oneMonth: '1개월',
        threeMonths: '3개월',
        initialize: '초기화',
        inquiry: '조회',
        salesTable: {
          no: 'NO',
          businessDate: '영업일',
          salesStatus: '매출 현황',
          paymentMethods: '결제 수단',
          totalSales: '총매출',
          totalDiscount: '총할인',
          netSales: '순매출',
          receiptCount: '영수증건수',
          avgValue: '객단가',
          cancelCount: '취소건수',
          cancelAmount: '취소금액',
          cash: '현금',
          card: '카드',
          easyPay: '간편결제',
          prepay: '선결제',
          other: '기타',
        },
        receipt: {
          list: '영수증 목록',
          print: '영수증 출력',
          details: '영수증 상세',
          no: 'NO',
          businessDate: '영업일',
          receiptNo: '영수증 번호',
          amount: '금액',
          phone: '전화번호',
          type: '유형',
          normal: '일반',
          return: '반품',
          preview: {
            title: '[ 영수증 ]',
            store: '[점포명]',
            bizNo: '[사업자번호]',
            addr: '[주소]',
            ceo: '[대표]',
            tel: '[전화]',
            date: '[판매일]',
            posNo: '[POS 번호]',
            item: '상품명',
            price: '단가',
            qty: '수량',
            amt: '금액',
            totalSales: '총 매출:',
            vat: '부가세:',
            service: '서비스:',
            total: '합계:',
            card: '카드:',
            cardNo: '번호:',
            apprNo: '승인번호:',
            merchantNo: '가맹점번호',
            apprDate: '승인일시:',
          }
        },
        staff: {
          title: '직원별 매출',
          staff: '직원',
          all: '전체',
          salesByStaff: '직원별 매출',
          salesDetails: '매출 상세 내역',
          no: 'NO',
          name: '이름',
          receipts: '영수증건수',
          totalSales: '총매출',
          netSales: '순매출',
          date: '일자',
          receiptNo: '영수증번호',
          type: '구분',
          total: '총액',
          discount: '할인',
          net: '순매출',
          normal: '일반',
          return: '반품',
        },
      },
      membership: {
        title: '회원 관리',
        tabs: {
          customer: '고객 관리',
          prepayment: '선불금 관리',
        },
        customer: {
          phone: '전화번호',
          name: '이름',
          reset: '초기화',
          inquiry: '조회',
          delete: '삭제',
          newMember: '신규 회원',
          no: '번호',
          customerNo: '고객 번호',
          grade: '등급',
          pointDetails: '포인트 상세',
          accumulated: '누적',
          used: '사용',
          available: '사용 가능',
          visitCount: '방문 횟수',
          prepaidBalance: '선불금 잔액',
          joinDate: '최초 가입일',
          showing: '보기',
          to: '부터',
          of: '까지 총',
          entries: '개 항목',
        },
        modal: {
          title: '고객 등록',
          customerNo: '고객 번호',
          name: '이름',
          phone: '전화번호',
          sms: 'SMS 수신',
          receive: '수신',
          reject: '거부',
          cancel: '취소',
          save: '저장',
        },
        prepayment: {
          title: '선불금 관리',
          initialize: '초기화',
          inquiry: '조회',
          phone: '전화번호',
          name: '이름',
          customerInfo: '고객 정보',
          grade: '등급',
          balance: '선불금 잔액',
          charge: '충전',
          cancelCharge: '충전 취소',
          chargeAmount: '충전 금액',
          balanceAfter: '충전 후 잔액',
          prepaymentHistory: '선불금 내역',
          usageHistory: '사용 내역',
          date: '날짜',
          type: '유형',
          total: '합계',
          cancelPossible: '취소 가능',
          possible: '가능',
          impossible: '불가능',
          receiptNo: '영수증 번호',
          usageAmount: '사용 금액',
        }
      },
      settings: {
        title: '환경 설정 - 매장 관리',
        tabs: {
          store: '매장 환경 설정',
          staff: '직원 관리',
          members: '회원 관리',
          hours: '영업 시간',
        },
        store: {
          repImage: '대표 이미지',
          coverImages: '커버 이미지',
          add: '추가',
          basicInfo: '기본 정보',
          edit: '수정',
          save: '저장',
          storeCodeName: '매장 코드/이름',
          repName: '대표자명',
          bizLicense: '사업자 등록번호',
          businessName: '상호명',
          bizType: '업종',
          phone: '전화번호',
          mobile: '휴대폰',
          address: '주소',
          search: '검색',
          deviceConfig: '장치 설정',
          vanCode: 'VAN 코드',
          tid: 'TID',
          comport: 'COM 포트',
          baudRate: 'Baud Rate',
          depositUsage: '예약금 사용',
          depositHelp: '예약 시 예약금 요구 활성화',
        },
        staff: {
          title: '직원 관리',
          newReset: '신규/초기화',
          delete: '삭제',
          save: '저장',
          searchPlaceholder: '이름, ID, 전화번호로 검색...',
          nameId: '이름 / ID',
          status: '상태',
          userInfo: '사용자 정보',
          workHours: '근무 시간',
          userInfoHelp: '개인 및 로그인 정보 관리',
          workHoursHelp: '주간 스케줄 설정',
          accountDetails: '계정 정보',
          userId: '사용자 ID',
          check: '확인',
          password: '비밀번호',
          authority: '권한',
          admin: '관리자',
          user: '일반 사용자',
          workStatus: '근무 상태',
          active: '재직 중',
          inactive: '퇴사',
          personalInfo: '개인 정보',
          staffName: '직원 이름',
          staffNo: '직원 번호',
          phone: '전화번호',
          email: '이메일',
          address: '주소',
          days: {
            mon: '월요일', tue: '화요일', wed: '수요일', thu: '목요일', fri: '금요일', sat: '토요일', sun: '일요일'
          }
        },
        members: {
          accDiscType: '적립/할인 구분',
          accumulation: '적립',
          discount: '할인',
          view: '보기',
          save: '저장',
          membershipLevels: '회원 등급',
          delete: '삭제',
          add: '추가',
          levelName: '등급명',
          criteriaAmount: '기준 금액',
          criteriaCount: '기준 횟수',
          accRate: '적립률',
          discRate: '할인율',
          flatRateMgmt: '정액권 관리',
          policyName: '정책명',
          paymentAmount: '결제 금액',
          accPoints: '적립 포인트',
          validityPeriod: '유효 기간',
          won: '원'
        },
        hours: {
          title: '환경 설정 - 영업 시간',
          settings: '영업 시간 설정',
          schedule: '월간 스케줄',
          save: '저장',
          operatingHours: '영업 시간',
          breakTime: '휴식 시간',
          annualTitle: '연간 휴일 설정',
          annualHelp: '반복되는 주간 휴일 설정',
          every: '매주',
          closed: '휴무'
        }
      },
      serviceSettings: {
        title: '환경 설정 - 서비스 관리',
        tabs: {
          service: '서비스 설정',
          option: '옵션 설정'
        },
        mainCategory: '주요 카테고리',
        subCategory: '하위 카테고리',
        optionSettings: '옵션 설정',
        categoryName: '카테고리명',
        usage: '사용 여부',
        sameAsMain: '주요 카테고리와 동일',
        serviceName: '서비스명',
        time: '시간',
        amount: '금액',
        use: '사용',
        unused: '미사용',
        noGroups: '그룹 없음',
        noCategories: '카테고리 없음',
        noOptions: '옵션 없음',
      },
      cancellation: {
        title: '취소 관리',
        search: {
          placeholder: '이름, 전화번호, 영수증 번호로 검색...',
        },
        actions: {
          reprint: '영수증 재발행',
          cancel: '취소 처리',
        },
        table: {
          no: '번호',
          date: '날짜/시간',
          receiptNo: '영수증 번호',
          customer: '고객',
          phone: '전화번호',
          staff: '담당자',
          status: '상태',
          amount: '금액',
        },
        status: {
          completed: '완료',
          pending: '대기 중',
        }
      },
      profile: {
        title: '내 프로필',
        userInfo: '사용자 정보',
        security: '보안',
        fullName: '전체 이름',
        email: '이메일',
        storeId: '매장 ID',
        updateProfile: '프로필 업데이트',
        changePassword: '비밀번호 변경',
        currentPassword: '현재 비밀번호',
        newPassword: '새 비밀번호',
        confirmPassword: '비밀번호 확인',
        updatePassword: '비밀번호 업데이트',
        edit: '수정',
        save: '저장',
        cancel: '취소',
      },
      common: {
        save: '저장',
        cancel: '취소',
        edit: '수정',
        delete: '삭제',
        search: '검색...',
        actions: '작업',
        reset: '초기화',
        close: '닫기',
        view: '보기',
        printReceipt: '영수증 출력',
      },
      language: {
        title: '언어 관리',
        addKey: '새 키 추가',
        editKey: '번역 수정',
        key: '번역 키',
        keyHelp: '점 표기법을 사용하여 중첩 (예: common.save). 키는 생성 후 변경할 수 없습니다.',
        enValue: '영어 값',
        krValue: '한국어 값',
        searchPlaceholder: '기록 검색...',
      },
    },
  });

  setLanguage(lang: 'en' | 'ko' | 'vi') {
    this.currentLanguage.set(lang);
  }

  t(key: string): string {
    const lang = this.currentLanguage();
    const keys = key.split('.');
    let result = this.translations()[lang];
    for (const k of keys) {
      result = result?.[k];
      if (result === undefined) {
        return key; // Return key if not found
      }
    }
    return result;
  }

  // --- Methods for LanguageSettingsComponent ---

  getFlatTranslations(): FlatTranslation[] {
    const enFlat = this.flattenTranslations(this.translations().en);
    const findValue = (obj: any, path: string) => path.split('.').reduce((o, i) => (o ? o[i] : undefined), obj);
    return enFlat.map((item) => ({ ...item, ko: findValue(this.translations().ko, item.key) || '' }));
  }

  updateTranslation(key: string, enValue: string, krValue: string) {
    this.translations.update((current) => {
      const newTranslations = JSON.parse(JSON.stringify(current));
      const keys = key.split('.');
      const setValue = (obj: any, path: string[], value: string) => {
        let current = obj;
        for (let i = 0; i < path.length - 1; i++) {
          const segment = path[i];
          if (!current[segment] || typeof current[segment] !== 'object') current[segment] = {};
          current = current[segment];
        }
        current[path[path.length - 1]] = value;
      };
      setValue(newTranslations.en, keys, enValue);
      setValue(newTranslations.ko, keys, krValue);
      return newTranslations;
    });
  }

  private flattenTranslations(obj: any, prefix = ''): Omit<FlatTranslation, 'ko'>[] {
    return Object.keys(obj).reduce((acc: Omit<FlatTranslation, 'ko'>[], k: string) => {
      const pre = prefix.length ? prefix + '.' : '';
      if (typeof obj[k] === 'object' && obj[k] !== null) {
        acc.push(...this.flattenTranslations(obj[k], pre + k));
      } else {
        acc.push({ key: pre + k, en: obj[k] });
      }
      return acc;
    }, []);
  }
}
